import { useState, useRef } from "react";
import { motion, useInView } from "framer-motion";
import { Send, Loader2, CheckCircle, Mail } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function SpeakingForm() {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-100px" });
  const [isLoading, setIsLoading] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [formData, setFormData] = useState({
    name: "",
    organization: "",
    email: "",
    phone: "",
    website: "",
    event_type: "",
    topic_of_interest: "",
    event_date: "",
    budget_range: "",
    event_format: "",
    audience_size: "",
    additional_notes: "",
  });

  const handleChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Only require name, email, phone
    if (!formData.name || !formData.email || !formData.phone) {
      toast.error("Please fill in name, email, and phone number");
      return;
    }

    setIsLoading(true);
    try {
      // Fill in defaults for non-required fields
      const submitData = {
        ...formData,
        organization: formData.organization || "Not specified",
        event_type: formData.event_type || "To be discussed",
        topic_of_interest: formData.topic_of_interest || "To be discussed",
        budget_range: formData.budget_range || "To be discussed",
        event_format: formData.event_format || "To be discussed",
      };
      
      const response = await axios.post(`${API}/speaking-inquiry`, submitData);
      
      if (response.data.status === "success") {
        setIsSuccess(true);
        toast.success(response.data.message);
      }
    } catch (error) {
      toast.error("Something went wrong. Please try again.");
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <section
      ref={ref}
      id="speaking-form"
      className="py-24 md:py-32 lg:py-40 px-6 md:px-12 lg:px-24 bg-card/30 relative"
      data-testid="speaking-form-section"
    >
      {/* Background */}
      <div className="absolute left-0 top-1/2 -translate-y-1/2 w-96 h-96 gradient-blob" />
      
      <div className="max-w-3xl mx-auto relative z-10">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.8 }}
          className="text-center mb-12"
        >
          <span className="text-xs font-['Orbitron'] uppercase tracking-[0.2em] text-primary mb-4 block">
            Book Shea
          </span>
          <h2 className="font-['Orbitron'] text-3xl sm:text-4xl lg:text-5xl font-bold uppercase tracking-tight text-foreground mb-6">
            Speaking <span className="gradient-text-purple">Inquiry</span>
          </h2>
          <p className="text-base sm:text-lg text-muted-foreground max-w-2xl mx-auto mb-4">
            Interested in having Shea speak at your event? Fill out the form below or email us directly.
          </p>
          <a 
            href="mailto:rcoke@futurewisegroup.com?subject=Speaking Inquiry for Shea Richburg"
            className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-primary/10 border border-primary/30 text-primary hover:bg-primary/20 transition-colors font-semibold"
          >
            <Mail size={20} />
            rcoke@futurewisegroup.com
          </a>
        </motion.div>

        {isSuccess ? (
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className="neon-card p-12 text-center"
          >
            <CheckCircle className="text-primary mx-auto mb-4" size={48} />
            <h3 className="font-['Orbitron'] text-2xl font-bold uppercase text-foreground mb-2">
              Inquiry Submitted!
            </h3>
            <p className="text-muted-foreground">
              Thank you for your interest. Shea's team will review your request and 
              get back to you soon.
            </p>
          </motion.div>
        ) : (
          <motion.form
            initial={{ opacity: 0, y: 30 }}
            animate={isInView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.8, delay: 0.2 }}
            onSubmit={handleSubmit}
            className="neon-card p-6 lg:p-10"
          >
            <div className="space-y-6">
              {/* Name */}
              <div>
                <label className="text-sm font-semibold uppercase tracking-wider text-foreground mb-2 block">
                  Your Name <span className="text-pink-500">*</span>
                </label>
                <Input
                  type="text"
                  value={formData.name}
                  onChange={(e) => handleChange("name", e.target.value)}
                  className="bg-background border-primary/30 focus:border-primary rounded-lg h-14 text-lg text-foreground placeholder:text-muted-foreground"
                  placeholder="Enter your full name"
                  data-testid="speaking-form-name"
                />
              </div>

              {/* Email */}
              <div>
                <label className="text-sm font-semibold uppercase tracking-wider text-foreground mb-2 block">
                  Email Address <span className="text-pink-500">*</span>
                </label>
                <Input
                  type="email"
                  value={formData.email}
                  onChange={(e) => handleChange("email", e.target.value)}
                  className="bg-background border-primary/30 focus:border-primary rounded-lg h-14 text-lg text-foreground placeholder:text-muted-foreground"
                  placeholder="your@email.com"
                  data-testid="speaking-form-email"
                />
              </div>

              {/* Phone */}
              <div>
                <label className="text-sm font-semibold uppercase tracking-wider text-foreground mb-2 block">
                  Phone Number <span className="text-pink-500">*</span>
                </label>
                <Input
                  type="tel"
                  value={formData.phone}
                  onChange={(e) => handleChange("phone", e.target.value)}
                  className="bg-background border-primary/30 focus:border-primary rounded-lg h-14 text-lg text-foreground placeholder:text-muted-foreground"
                  placeholder="+1 (555) 000-0000"
                  data-testid="speaking-form-phone"
                />
              </div>

              {/* Additional Notes */}
              <div>
                <label className="text-sm font-semibold uppercase tracking-wider text-foreground mb-2 block">
                  Tell Us About Your Event
                </label>
                <Textarea
                  value={formData.additional_notes}
                  onChange={(e) => handleChange("additional_notes", e.target.value)}
                  className="bg-background border-primary/30 focus:border-primary rounded-lg min-h-[150px] text-lg text-foreground placeholder:text-muted-foreground"
                  placeholder="Share details about your event, audience, date, topic of interest, budget, and any other information that would be helpful..."
                  data-testid="speaking-form-notes"
                />
              </div>
            </div>

            {/* Submit Button */}
            <div className="mt-8">
              <Button
                type="submit"
                disabled={isLoading}
                className="w-full bg-primary text-primary-foreground rounded-full h-16 font-bold uppercase tracking-wider text-lg hover:bg-primary/90 transition-all btn-glow"
                data-testid="speaking-form-submit-btn"
              >
                {isLoading ? (
                  <Loader2 className="animate-spin" size={24} />
                ) : (
                  <>
                    <Send size={22} className="mr-3" />
                    Submit Speaking Inquiry
                  </>
                )}
              </Button>
            </div>
          </motion.form>
        )}
      </div>
    </section>
  );
}
